'use client'

import useSportMarketQuery from '@/Overtime/queries/useSportMarketV2Query'
import type { SportMarket } from '@/Overtime/types/markets'
import React, { useEffect, useMemo, useState } from 'react'
import { EventInfoSkeleton } from '@/compositions/event/EventInfo/EventInfo'
import { MarketsSkeleton } from '@/compositions/event/Markets/Markets'
import OvertimeEventInfo from '@/compositions/event/OvertimeEventInfo/OvertimeEventInfo'
import { getErrorImage, getTeamImageSource } from '@/Overtime/utils/images'
import OvertimeMarkets from '@/compositions/event/Markets/OvertimeMarkets'
import { useChainId } from 'wagmi'
import type { SupportedNetwork } from '@/Overtime/types/network'

interface SingleGameContentProps {
  marketAddress: string
}

const createSlug = (text: string) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

function getLastWordAfterComma(str: string) {
  return str.split(',').pop()?.trim() || ''
}

export const useGameInfo = (lastValidMarket: SportMarket) => {
  const {
    homeTeam,
    awayTeam,
    leagueId,
    tournamentName,
    gameId,
    maturityDate,
    sport,
    leagueName,
    liveScore,
  } = lastValidMarket

  const [homeLogoSrc, setHomeLogoSrc] = useState(
    getTeamImageSource(homeTeam, leagueId)
      ? getTeamImageSource(homeTeam, leagueId)
      : getErrorImage(leagueId)
  )
  const [awayLogoSrc, setAwayLogoSrc] = useState(
    getTeamImageSource(awayTeam, leagueId)
      ? getTeamImageSource(awayTeam, leagueId)
      : getErrorImage(leagueId)
  )

  const gameInfo = useMemo(
    () => ({
      id: gameId,
      gameId: gameId,
      leagueId: leagueId,
      slug: `${createSlug(homeTeam ?? '')}-${createSlug(
        awayTeam ?? ''
      )}-${gameId}`,
      title: `${homeTeam} – ${awayTeam}`,
      startsAt: maturityDate,
      turnover: '0',
      sport: {
        sportId: '33',
        slug: createSlug(sport ?? ''),
        name: sport,
        sporthub: {
          id: 'sports',
          slug: 'sports',
        },
      },
      league: {
        slug: createSlug(leagueName ?? ''),
        name: leagueName,
      },
      country: {
        slug: createSlug(getLastWordAfterComma(tournamentName ?? '')),
        name: getLastWordAfterComma(tournamentName ?? ''),
      },
      participants: [
        {
          image: homeLogoSrc,
          name: homeTeam,
        },
        {
          image: awayLogoSrc,
          name: awayTeam,
        },
      ],
      liveScore,
    }),
    [lastValidMarket, homeLogoSrc, awayLogoSrc]
  )

  return {
    gameInfo,
    homeLogoSrc,
    awayLogoSrc,
    setHomeLogoSrc,
    setAwayLogoSrc,
  }
}

const Content: React.FC<any> = ({ lastValidMarket }) => {
  const { gameInfo } = useGameInfo(lastValidMarket)

  const {
    isCancelled,
    isResolved,
    isGameFinished,
    gameStatus,
    isPaused,
    liveScore,
    isPendingResolution,
  } = lastValidMarket

  // const status = isCancelled
  //   ? 'Cancelled'
  //   : isResolved || isGameFinished
  //     ? showGameScore(gameStatus) || !gameStatus
  //       ? 'GAME SCORE'
  //       : GameStatusKey[gameStatus || '']
  //     : isPendingResolution
  //       ? liveScore
  //         ? showGameScore(liveScore.gameStatus) || !liveScore.gameStatus
  //           ? 'SHOW LIVE GAME SCORE'
  //           : GameStatusKey[liveScore.gameStatus || '']
  //         : 'Pending'
  //       : isPaused
  //         ? 'Paused'
  //         : 'Prematch'

  const gameState = isGameFinished
    ? 'Finished'
    : liveScore === ''
      ? 'Prematch'
      : 'Live'

  return (
    <>
      <OvertimeEventInfo game={gameInfo! as any} state={gameState as any} />
      <OvertimeMarkets
        gameState={gameState as any}
        market={lastValidMarket! as any}
      />
    </>
  )
}

const SingleGameContent = ({ marketAddress }: SingleGameContentProps) => {
  const chainId = useChainId()
  const [lastValidMarket, setLastValidMarket] = useState<
    SportMarket | undefined
  >(undefined)
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true)
  const [isGameNotFound, setIsGameNotFound] = useState<boolean>(false)

  const singleMarketQuery = useSportMarketQuery(
    marketAddress,
    false,
    false,
    {
      networkId: chainId as SupportedNetwork,
    },
    {
      enabled: !isGameNotFound,
    }
  )

  const { isFetching } = singleMarketQuery

  useEffect(() => {
    if (singleMarketQuery.isSuccess) {
      if (singleMarketQuery.data) {
        console.log({ singleMarketQuery: singleMarketQuery.data })
        setLastValidMarket(singleMarketQuery.data)
        setIsGameNotFound(false)
        setIsFirstRender(false)
      }
    }
    if (singleMarketQuery.isError) {
      setIsGameNotFound(true)
      setIsFirstRender(false)
    }
  }, [
    marketAddress,
    singleMarketQuery.isSuccess,
    singleMarketQuery.isError,
    singleMarketQuery.data,
  ])

  if (isFetching && isFirstRender) {
    return (
      <>
        <EventInfoSkeleton />
        <MarketsSkeleton />
      </>
    )
  }

  if (!lastValidMarket) {
    return <div>Game info not found</div>
  }

  console.log({ lastValidMarket })

  return <Content lastValidMarket={lastValidMarket} />
}

export default SingleGameContent
